<template>
  <div class="desk">
    <div class="head">
      <div>
        <h1>工作台看板</h1>
        <p>数据范围：全平台 · 统计周期 {{ periodText }} · 更新于 {{ updatedAt }}</p>
      </div>
      <div class="actions">
        <button class="ghost" type="button" @click="exportReport"><i class="el-icon-upload2" /> 导出月报</button>
        <button class="gold" type="button" @click="$router.push('/hq/partners')"><i class="el-icon-plus" /> 开设渠道账号</button>
      </div>
    </div>

    <section class="cards">
      <article>
        <div class="card-top"><span>本月分账总额</span><i class="el-icon-top-right up" /></div>
        <div class="num">¥ {{ money(monthCommission) }}</div>
        <p>{{ compareText }} · 含税未配置 · 成本（算力花费）未配置</p>
      </article>
      <article>
        <div class="card-top"><span>本月积分采购额</span><i class="el-icon-link" /></div>
        <div class="num">¥ {{ money(monthPurchase) }}</div>
        <p>购买代金券 {{ monthPurchaseCount }} 笔 · 待确认到账 {{ pendingPay }} 笔</p>
      </article>
      <article>
        <div class="card-top"><span>待处理事项</span><i class="el-icon-time" /></div>
        <div class="num">{{ todo }} <small>项</small></div>
        <p>账号申请 {{ pendingPartners }} · 发券 {{ pendingVouchers }} · 提现 {{ pendingWithdraw }} · 报备 {{ pendingReports }}</p>
      </article>
      <article>
        <div class="card-top"><span>订单预警</span><i class="el-icon-warning-outline warn" /></div>
        <div class="num">0 <small>条</small></div>
        <p>窜货 · 低价 · 套奖尚未接入</p>
      </article>
    </section>

    <section class="mid">
      <article class="panel">
        <div class="panel-head">
          <h2>近 6 个月分账与采购趋势</h2>
          <span>单位：万元</span>
        </div>
        <div ref="line" class="line" />
      </article>
      <article class="panel">
        <div class="panel-head">
          <h2>五级渠道采购占比</h2>
          <span>按购买代金券面值</span>
        </div>
        <div ref="pie" class="pie" />
      </article>
    </section>

    <section class="bottom">
      <article class="panel">
        <div class="panel-head">
          <h2>本月渠道分账 TOP 8</h2>
          <button class="link" type="button" @click="$router.push('/hq/settlements')">查看全部结算单 →</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>排名</th><th>渠道商</th><th>级别</th><th>采购额</th><th>已使用积分</th>
              <th>成本（算力花费）</th><th>分账金额</th><th>达标进度</th><th>状态</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in top" :key="row.id">
              <td class="rank">{{ index + 1 }}</td>
              <td>{{ row.name }}</td>
              <td><span class="lv" :class="row.levelCode">{{ levelName(row.levelCode) }}</span></td>
              <td>¥ {{ money(row.purchase) }}</td>
              <td>{{ points(row.points) }}</td>
              <td>—</td>
              <td>¥ {{ money(row.commission) }}</td>
              <td>未配置</td>
              <td><span class="st" :class="row.settleStatus">{{ row.settleText }}</span></td>
            </tr>
            <tr v-if="!top.length"><td colspan="9" class="empty">本月还没有渠道分账</td></tr>
          </tbody>
        </table>
      </article>
      <article class="panel">
        <div class="panel-head">
          <h2>待办事项</h2>
          <b>{{ todo }} 项</b>
        </div>
        <button class="todo" type="button" @click="$router.push('/hq/partners')">
          <i class="el-icon-user" />
          <span><b>渠道账号开通申请待审核</b><small>{{ pendingPartnerText }}</small></span>
          <em>{{ pendingPartners }}</em>
        </button>
        <button class="todo" type="button" @click="$router.push('/hq/vouchers')">
          <i class="el-icon-bank-card" />
          <span><b>代金券确认与发放</b><small>待确认到账 {{ pendingPay }} · 待审批 {{ pendingReview }}</small></span>
          <em>{{ pendingVouchers }}</em>
        </button>
        <button class="todo" type="button" @click="$router.push('/hq/settlements')">
          <i class="el-icon-wallet" />
          <span><b>提现与结算</b><small>提现 {{ pendingWithdraw }} 笔 · 待确认结算 {{ pendingSettle }} 张</small></span>
          <em>{{ pendingWithdraw + pendingSettle }}</em>
        </button>
        <button class="todo" type="button" @click="$router.push('/hq/reports')">
          <i class="el-icon-document" />
          <span><b>客户报备审核</b><small>保护期冲突仲裁还没开通</small></span>
          <em>{{ pendingReports }}</em>
        </button>
      </article>
    </section>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart, PieChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { getChannelLines, getChannelPartners, getChannelReports, getChannelSettlements, getChannelVouchers, getChannelWithdrawals } from '@/api/admin'
import { levelName, statusName } from '../partner/labels'

echarts.use([LineChart, PieChart, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

const PIE_COLORS = ['#1e3a8a', '#3b6fd8', '#60a5fa', '#93c5fd', '#f0b429']

export default {
  name: 'HqBoard',
  data() {
    return {
      partners: [], vouchers: [], settlements: [], lines: [], withdrawals: [], reports: [],
      updatedAt: '', lineChart: null, pieChart: null
    }
  },
  computed: {
    monthKey() {
      const now = new Date()
      return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0')
    },
    prevKey() {
      const now = new Date()
      const date = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0')
    },
    periodText() {
      const now = new Date()
      const start = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-01'
      const end = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0')
      return start + ' 至 ' + end
    },
    monthLines() { return this.lines.filter(item => this.inMonth(item.createdAt, this.monthKey)) },
    prevLines() { return this.lines.filter(item => this.inMonth(item.createdAt, this.prevKey)) },
    monthCommission() { return this.sum(this.monthLines, 'amount') },
    prevCommission() { return this.sum(this.prevLines, 'amount') },
    compareText() {
      if (!this.prevCommission) return '较上月无上月基数'
      const pct = (this.monthCommission - this.prevCommission) / this.prevCommission * 100
      const sign = pct > 0 ? '+' : ''
      return '较上月 ' + sign + pct.toFixed(1) + '%'
    },
    monthPurchases() {
      return this.vouchers.filter(item => item.source === 'PURCHASE' && this.inMonth(item.createdAt, this.monthKey))
    },
    monthPurchase() { return this.sum(this.monthPurchases, 'faceValue') },
    monthPurchaseCount() { return this.monthPurchases.length },
    pendingPartners() { return this.partners.filter(item => item.status === 'PENDING').length },
    pendingPay() { return this.vouchers.filter(item => item.status === 'PENDING_PAY').length },
    pendingReview() { return this.vouchers.filter(item => item.status === 'PENDING_REVIEW').length },
    pendingVouchers() { return this.pendingPay + this.pendingReview },
    pendingWithdraw() {
      return this.withdrawals.filter(item => ['PAID', 'DONE', 'REJECTED'].indexOf(item.status) < 0).length
    },
    pendingSettle() { return this.settlements.filter(item => item.status === 'PENDING_CONFIRM').length },
    pendingReports() { return this.reports.filter(item => item.status === 'PENDING').length },
    todo() { return this.pendingPartners + this.pendingVouchers + this.pendingWithdraw + this.pendingReports },
    pendingPartnerText() {
      const row = this.partners.find(item => item.status === 'PENDING')
      return row ? '最早待审：' + (row.name || '') : '当前没有待审开户'
    },
    top() {
      return this.partners
        .map(item => {
          const ownLines = this.monthLines.filter(line => String(line.partnerId) === String(item.id))
          const ownBuy = this.monthPurchases.filter(voucher => String(voucher.partnerId) === String(item.id))
          const settlement = this.settlements.find(row => String(row.partnerId) === String(item.id) && row.periodMonth === this.monthKey)
          return {
            id: item.id,
            name: item.name,
            levelCode: item.levelCode,
            purchase: this.sum(ownBuy, 'faceValue'),
            points: this.sum(ownLines, 'points'),
            commission: this.sum(ownLines, 'amount'),
            settleStatus: settlement ? settlement.status : '',
            settleText: settlement ? statusName(settlement.status) : '未出单'
          }
        })
        .sort((a, b) => b.commission - a.commission || b.purchase - a.purchase)
        .slice(0, 8)
    },
    levelShare() {
      const codes = ['INDUSTRY', 'PROVINCE', 'CITY', 'DISTRICT', 'ECO']
      return codes.map(code => {
        const ids = this.partners.filter(item => item.levelCode === code).map(item => String(item.id))
        const value = this.monthPurchases
          .filter(item => ids.indexOf(String(item.partnerId)) >= 0)
          .reduce((sum, item) => sum + Number(item.faceValue || 0), 0)
        return { name: levelName(code), value }
      })
    }
  },
  created() { this.load() },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    if (this.lineChart) this.lineChart.dispose()
    if (this.pieChart) this.pieChart.dispose()
  },
  methods: {
    levelName,
    inMonth(value, key) { return String(value || '').indexOf(key) === 0 },
    sum(rows, field) { return rows.reduce((total, item) => total + Number(item[field] || 0), 0) },
    money(value) {
      return Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    points(value) { return Number(value || 0).toLocaleString('zh-CN') },
    async load() {
      const [partners, vouchers, settlements, lines, withdrawals, reports] = await Promise.all([
        getChannelPartners(), getChannelVouchers(), getChannelSettlements(),
        getChannelLines(), getChannelWithdrawals(), getChannelReports()
      ])
      this.partners = partners.list || []
      this.vouchers = vouchers.list || []
      this.settlements = settlements.list || []
      this.lines = lines.list || []
      this.withdrawals = withdrawals.list || []
      this.reports = reports.list || []
      const now = new Date()
      this.updatedAt = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0')
      this.$nextTick(this.draw)
    },
    months() {
      const list = []
      const now = new Date()
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        list.push({
          key: date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0'),
          label: (date.getMonth() + 1) + '月'
        })
      }
      return list
    },
    draw() {
      this.drawLine()
      this.drawPie()
      window.addEventListener('resize', this.resize)
    },
    drawLine() {
      if (!this.$refs.line) return
      if (!this.lineChart) this.lineChart = echarts.init(this.$refs.line)
      const months = this.months()
      const commission = months.map(month => this.lines.filter(item => this.inMonth(item.createdAt, month.key)).reduce((sum, item) => sum + Number(item.amount || 0), 0) / 10000)
      const purchase = months.map(month => this.vouchers.filter(item => item.source === 'PURCHASE' && this.inMonth(item.createdAt, month.key)).reduce((sum, item) => sum + Number(item.faceValue || 0), 0) / 10000)
      this.lineChart.setOption({
        color: ['#3b6fd8', '#e2b340'],
        tooltip: { trigger: 'axis' },
        legend: { right: 0, top: 0, data: ['分账金额', '积分采购额'], textStyle: { color: '#667085', fontSize: 12 } },
        grid: { left: 48, right: 16, top: 36, bottom: 28 },
        xAxis: { type: 'category', boundaryGap: false, data: months.map(item => item.label), axisLine: { lineStyle: { color: '#e6eaf2' } }, axisLabel: { color: '#98a2b3' } },
        yAxis: { type: 'value', name: '万元', nameTextStyle: { color: '#98a2b3' }, splitLine: { lineStyle: { color: '#eef1f6' } }, axisLabel: { color: '#98a2b3' } },
        series: [
          { name: '分账金额', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, areaStyle: { color: 'rgba(59,111,216,0.12)' }, data: commission },
          { name: '积分采购额', type: 'line', smooth: true, symbol: 'circle', symbolSize: 6, lineStyle: { type: 'dashed' }, data: purchase }
        ]
      })
    },
    drawPie() {
      if (!this.$refs.pie) return
      if (!this.pieChart) this.pieChart = echarts.init(this.$refs.pie)
      const total = this.levelShare.reduce((sum, item) => sum + item.value, 0)
      const data = total
        ? this.levelShare.map((item, index) => ({ name: item.name, value: item.value, itemStyle: { color: PIE_COLORS[index] } }))
        : [{ name: '本月暂无采购', value: 1, itemStyle: { color: '#e7ebf3' } }]
      this.pieChart.setOption({
        tooltip: { trigger: 'item', formatter: total ? '{b}<br/>¥ {c}' : '{b}' },
        legend: { bottom: 0, left: 'center', itemWidth: 8, itemHeight: 8, textStyle: { color: '#667085', fontSize: 12 } },
        series: [{
          type: 'pie',
          radius: ['52%', '74%'],
          center: ['50%', '42%'],
          label: { show: false },
          data
        }]
      })
    },
    resize() {
      if (this.lineChart) this.lineChart.resize()
      if (this.pieChart) this.pieChart.resize()
    },
    exportReport() {
      const header = ['排名', '渠道商', '级别', '采购额', '已使用积分', '分账金额', '达标进度', '状态']
      const body = this.top.map((row, index) => [index + 1, row.name, levelName(row.levelCode), row.purchase, row.points, row.commission, '未配置', row.settleText])
      const csv = [header].concat(body).map(line => line.join(',')).join('\n')
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '渠道月报-' + this.monthKey + '.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    }
  }
}
</script>

<style scoped>
.desk { color: #1f2a44; }
.head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; margin-bottom: 14px; }
h1 { margin: 0; font-size: 20px; color: #1a2468; }
.head p, .cards p, .todo small { margin: 6px 0 0; color: #8b93a7; font-size: 12px; line-height: 1.5; }
.actions { display: flex; gap: 8px; }
.ghost, .gold { height: 34px; padding: 0 14px; border-radius: 8px; font-size: 13px; cursor: pointer; }
.ghost { border: 1px solid #d9dfec; background: #fff; color: #1a2468; }
.gold { border: 0; background: #f0b429; color: #1a2468; font-weight: 700; }
.cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.cards article, .panel { background: #fff; border: 1px solid #e7ebf3; border-radius: 12px; }
.cards article { padding: 16px 18px 14px; }
.card-top { display: flex; justify-content: space-between; color: #8b93a7; font-size: 13px; }
.card-top .up { color: #16a34a; }
.card-top .warn { color: #e11d48; }
.num { margin-top: 10px; font-size: 26px; font-weight: 700; color: #1a2468; letter-spacing: -0.4px; }
.num small { font-size: 14px; font-weight: 600; margin-left: 4px; }
.mid, .bottom { display: grid; grid-template-columns: 1.75fr 0.9fr; gap: 12px; margin-top: 12px; }
.panel { padding: 16px 16px 8px; }
.panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
h2 { margin: 0; font-size: 15px; color: #1a2468; }
.panel-head span, .panel-head b { color: #8b93a7; font-size: 12px; font-weight: 500; }
.link { border: 0; background: none; color: #2f4dbf; cursor: pointer; font-size: 13px; }
.line { height: 280px; }
.pie { height: 280px; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th { text-align: left; color: #8b93a7; font-weight: 500; padding: 8px 6px; border-bottom: 1px solid #eef1f6; white-space: nowrap; }
td { padding: 12px 6px; border-bottom: 1px solid #f3f5f8; }
.rank { color: #1a2468; font-weight: 700; }
.lv, .st { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; background: #eef1fb; color: #2f4dbf; }
.lv.INDUSTRY { background: #e8f0ff; color: #2457d6; }
.lv.PROVINCE { background: #efe8ff; color: #6d28d9; }
.lv.CITY { background: #e7f8ef; color: #15803d; }
.lv.DISTRICT { background: #fff4e5; color: #b45309; }
.lv.ECO { background: #f3f4f6; color: #4b5563; }
.st.PAID, .st.DONE, .st.CONFIRMED { background: #e8f8ef; color: #15803d; }
.st.PENDING_CONFIRM, .st.REVIEWED, .st.INVOICED, .st.VERIFIED { background: #fff6e5; color: #9a6b00; }
.empty { text-align: center; color: #98a0b3; padding: 28px 8px; }
.todo { width: 100%; display: flex; align-items: center; gap: 10px; text-align: left; border: 0; border-bottom: 1px solid #f3f5f8; background: #fff; padding: 12px 4px; cursor: pointer; }
.todo i { width: 28px; height: 28px; border-radius: 8px; background: #eef2ff; color: #1a2468; display: flex; align-items: center; justify-content: center; }
.todo span { flex: 1; }
.todo b { display: block; color: #1f2a44; font-size: 13px; }
.todo em { min-width: 22px; height: 22px; border-radius: 11px; background: #f3f5fb; color: #1a2468; font-style: normal; font-size: 12px; line-height: 22px; text-align: center; }
@media (max-width: 1100px) {
  .cards, .mid, .bottom { grid-template-columns: 1fr; }
}
</style>
